import { useLocation } from 'react-router-dom'

import {
  parseQueryParams,
  serializeQueryParams,
} from '~publish/hooks/useQueryParam'
import { useScheduleLastView } from '~publish/hooks/useScheduleLastView'
import {
  channelSchedule,
  newCalendarSingleChannel,
  schedule,
  scheduleCalendar,
} from '~publish/legacy/routes'

import { filterQueryParamsByRoute } from './utils/filterQueryParamsByRoute'

/**
 * Build schedule URL with preserved filter params (channels, tags, tab)
 *
 * This hook ensures that when navigating to the "Schedule" section (new IA overhaul),
 * the selected filters (like channel filters, tag filters) persist in the URL.
 *
 * It also respects the user's view preference - if they prefer calendar view,
 * it returns the calendar URL instead of the list URL.
 *
 * When a singleChannelId is provided, it returns a channel-specific URL instead
 * of the aggregated schedule view.
 *
 * @param singleChannelId - Optional channel ID to link directly to a specific channel
 * @returns URL string with preserved query parameters
 *
 * @example
 * ```tsx
 * const allChannelsUrl = useAllChannelsUrl()
 * // If user prefers calendar: /schedule/calendar/week?channels[]=123
 * // If user prefers list: /schedule?channels[]=123
 *
 * const singleChannelUrl = useAllChannelsUrl('channel-123')
 * // If user prefers calendar: /channels/channel-123/calendar/week
 * // If user prefers list: /channels/channel-123/schedule
 * ```
 */
export function useAllChannelsUrl(singleChannelId?: string): string {
  const location = useLocation()
  const { prefersCalendar, calendarGranularity } = useScheduleLastView()
  const currentSearch = parseQueryParams(location.search)

  // When a single channel is provided, link directly to that channel's schedule
  if (singleChannelId) {
    if (prefersCalendar) {
      const filteredParams = filterQueryParamsByRoute(
        currentSearch,
        newCalendarSingleChannel.queryParams,
      )
      const queryString = serializeQueryParams(filteredParams)
      return (
        newCalendarSingleChannel.buildPathname({
          id: singleChannelId,
          granularity: calendarGranularity,
        }) + (queryString ? `?${queryString}` : '')
      )
    }

    const filteredParams = filterQueryParamsByRoute(
      currentSearch,
      channelSchedule.queryParams,
    )
    const queryString = serializeQueryParams(filteredParams)
    return (
      channelSchedule.buildPathname({ id: singleChannelId }) +
      (queryString ? `?${queryString}` : '')
    )
  }

  if (prefersCalendar) {
    // Filter current search params to only include those valid for calendar routes
    const filteredParams = filterQueryParamsByRoute(
      currentSearch,
      scheduleCalendar.queryParams,
    )

    const queryString = serializeQueryParams(filteredParams)
    return (
      scheduleCalendar.buildPathname({ granularity: calendarGranularity }) +
      (queryString ? `?${queryString}` : '')
    )
  }

  // Filter current search params to only include those valid for schedule routes
  const filteredParams = filterQueryParamsByRoute(
    currentSearch,
    schedule.queryParams,
  )

  const queryString = serializeQueryParams(filteredParams)
  return schedule.buildPathname() + (queryString ? `?${queryString}` : '')
}
